
function noop () {}

export interface HookType {
  // 钩子函数
  fn: Function;
  // 优先级
  priority?: number;
};

export type QueueType = {
  [key: string]: {
    [key: string]: HookType[];
  }
};

/**
 * 钩子队列
 */
const queue: QueueType = {};

/**
 * 注册钩子函数
 * @param {String} app 应用名称，形如：@weapp/ui, @weapp/utils
 * @param {string} name 钩子名称，形如：beforeSave, afterSave
 * @param {Function | HookType} fn 钩子函数
 * @param {number} priority 钩子优先级，数字越大优先级越高，默认为0
 */
function regHook(
  app: string,
  name: string,
  fn: Function | HookType,
  priority = 0,
) {
  // 将钩子加入到对应的数组中
  let fns = getHooks(app, name) || [];
  if (!Array.isArray(fns)) {
    fns = []; // 重置成数组
  }
  // 处理默认值
  let hook: HookType = { fn: noop, priority, };
  if (typeof fn === 'object' && fn?.fn) {
    hook = { ...hook, ...(fn as HookType) };
  } else {
    hook = { ...hook, fn: fn as Function };
  }
  fns.push(hook);
  // 根据优先级排序
  fns.sort((x: any, y: any) => x.priority <= y.priority ? 1 : -1);
  setHooks(app, name, fns);
}

/**
 * 移除已经注册的钩子
 * @param {String} app 应用名称，形如：@weapp/ui, @weapp/utils
 * @param {string} name 钩子名称，形如：beforeSave, afterSave
 * @param {Function | HookType} fn 钩子函数
 * @returns 
 */
function unRegHook (
  app: string,
  name: string,
  fn: Function | HookType,
): boolean {
  let fns = getHooks(app, name) || [];
  if (!Array.isArray(fns)) {
    fns = [];
  }
  if (fns.length === 0) return false;
  if (fns.findIndex(f => f.fn === fn) !== -1) {
    const removedHooks = fns.filter(f => f.fn !== fn);
    setHooks(app, name, removedHooks);
    return true;
  } else {
    return false;
  }
}

/**
 * 获取已注册的钩子
 * @param {string} app 应用名称，形如：@weapp/ui, @weapp/utils
 * @param {string} name 钩子名称，形如：beforeSave, afterSave
 * @returns 已经注册的钩子
 */
function getHooks(
  app?: string,
  name?: string,
): QueueType | { [key: string]: HookType[] } | HookType[] | undefined {
  // const n = app ? name ? `${app}.${name}` : app : '';
  // if (!n) return queue;
  // const pos = n.split('.');
  // return pos.reduce((d: QueueType | HookType[] | undefined, key: string) => {
  //   if (Array.isArray(d)) {
  //     return d;
  //   } else if (d && key in d) {
  //     return d[key];
  //   }
  //   return d;
  // }, queue);
  if (app && name && queue[app]) return queue[app][name] || queue[app];
  if (app) return queue[app] || queue;
  return queue;
}

/**
 * 设置钩子
 * @param {string} app 应用名称，形如：@weapp/ui, @weapp/utils
 * @param {string} name 钩子名称，形如：beforeSave, afterSave
 * @param hook 需要设置的钩子
 */
function setHooks(
  app: string,
  name: string,
  hook: HookType[],
) {
  // const n = app ? name ? `${app}.${name}` : app : '';
  // if (!n) return;
  // const pos = n.split('.');
  // const posLen = pos.length;
  // let curr: QueueType = queue;
  // pos.forEach((key, index) => {
  //   if (posLen - 1 === index) {
  //     curr[key] = hook;
  //   } else {
  //     const oldValue = curr[key];
  //     if (oldValue && !Array.isArray(oldValue)) {
  //       curr = oldValue;
  //     } else {
  //       curr[key] = {};
  //       curr = curr[key] as QueueType;
  //     }
  //   }
  // });
  if (!app || !name) return;

  if (app in queue) {
    queue[app][name] = hook
  } else {
    queue[app] = { [name]: hook };
  }
}

/**
 * 以同步链的方式获取钩子
 * @param {string} app 应用名称，形如：@weapp/ui, @weapp/utils
 * @param {string} name 钩子名称，形如：beforeSave, afterSave
 * @returns
 */
function getSyncHook<Data = any> (app: string, name: string): null | ((data: Data) => Data) {
  const hooks = getHooks(app, name) || [];
  if (!Array.isArray(hooks) || !hooks.length) return null;
  return [...hooks].reverse().reduce((next, hook) =>
    (data: Data) => {
      var newData = hook.fn?.(data) || data;
      return next?.(newData) || newData;
    }
  , (data: Data) => data);
}

/**
 * 以异步链的方式获取钩子 - Callback
 * @param {string} app 应用名称，形如：@weapp/ui, @weapp/utils
 * @param {string} name 钩子名称，形如：beforeSave, afterSave
 * @returns
 */
function getAsyncHook<Data = any> (app: string, name: string): null | ((data: Data) => Data) {
  const hooks = getHooks(app, name) || [];
  if (!Array.isArray(hooks) || !hooks.length) return null;
  return [...hooks].reverse().reduce((next, hook) =>
    (data: Data) => (hook.fn?.(data, next) || data)
  , (data: Data) => data);
}

/**
 * 以异步链的方式获取钩子 - Promise
 * @param {string} app 应用名称，形如：@weapp/ui, @weapp/utils
 * @param {string} name 钩子名称，形如：beforeSave, afterSave
 * @returns
 */
function getPromiseHook<Data = any> (app: string, name: string): null | ((data: Data) => Promise<Data>) {
  const hooks = getHooks(app, name) || [];
  if (!Array.isArray(hooks) || !hooks.length) return null;
  return (data: Data) => hooks.reduce((chain, hook) =>
    chain.then((data) => hook.fn?.(data))
  , Promise.resolve(data));
}

export {
  regHook,
  getHooks,
  setHooks,
  unRegHook,
  getSyncHook,
  getAsyncHook,
  getPromiseHook,
};
